/**
 * UGM-version capability helpers for the Deposit Protection picker.
 *
 * Background: the onchain Boardroom (UGM) shipped a few times. The
 * V2.1 deploys (immutable, non-proxy) gate `addDeposit(...)` behind a
 * strict `msg.sender == seat.holder` check. The pull-then-compose
 * `ProtectionRedeemer` performs `addDeposit` from its own address, so
 * any redemption against a V2.1 UGM reverts with `NotSeatHolder()`.
 *
 * Wallets that go through the `'7710-direct'` rail are unaffected —
 * that path uses `BoardroomKeeperFeeAdapter.redeemAndFlush(...)`,
 * which redeems the per-row delegation directly via the DM and the
 * delegator (the seat holder) is `msg.sender` to the UGM. Only the
 * `'7715-grant'` (MetaMask) rail breaks on V2.1.
 *
 * The list of V2.1 UGMs is configured via
 * `NEXT_PUBLIC_PROTECTION_V21_UGMS` — a comma-separated set of
 * checksum-or-lowercase EVM addresses. Treating it as data rather
 * than hard-coding a list keeps the gate testable and lets ops add
 * future immutable UGM deploys without a code change.
 *
 * Pure module (no React, no env reads at import time) so the dialog
 * AND the server-side enable route can both consume the same source
 * of truth. Callers pass `process.env.NEXT_PUBLIC_PROTECTION_V21_UGMS`
 * (literal access, see `use-deposit-protection.ts` docblock for why
 * literal access matters with Next's `DefinePlugin`).
 */

import type { Address } from 'viem';
import { getAddress } from 'viem';
import type { DelegationSigningPath } from '@/lib/wallet-capabilities';

/**
 * Parse the comma-separated env into a canonical lower-case set.
 * Empty / unset env yields an empty set — the gate then treats every
 * UGM as protectable, which matches the rollout intent (we only
 * gate boards we KNOW are V2.1; assume V2.2+ otherwise).
 */
export function parseV21UgmSet(raw: string | undefined | null): Set<string> {
  if (!raw) return new Set();
  return new Set(
    raw
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((s) => {
        // `getAddress` validates EIP-55; we then lower-case so the
        // membership check is case-insensitive without a per-call
        // `.toLowerCase()` on the test side.
        try {
          return getAddress(s).toLowerCase();
        } catch {
          // Skip malformed entries silently — operator misconfig
          // shouldn't crash the dialog. Logged once at boot via the
          // route's startup probe.
          return '';
        }
      })
      .filter((s) => s.length > 0),
  );
}

export interface UgmCompatibility {
  /**
   * Whether the given UGM can be redeemed against by the configured
   * `signingPath`. False for `'7715-grant'` against a V2.1 UGM.
   */
  protectable: boolean;
  /**
   * Short, user-facing tag for the dialog's "why is this disabled"
   * tooltip. Always populated when `protectable === false`.
   */
  reason: 'v21-incompatible-with-7715' | null;
}

/**
 * Decide whether a holder on `signingPath` can include `ugm` in their
 * delegation's `protectedUgms`. The two terminal cases:
 *
 *   - `7715-grant` + UGM in `v21Ugms` → not protectable.
 *   - everything else                 → protectable.
 *
 * Keeping the function shape `Compatibility` (rather than a bare
 * boolean) lets the UI render a reason chip per-row without a parallel
 * lookup, and a future "v22 with quirk X" gate can extend the discriminant
 * without breaking the call sites.
 */
export function getUgmCompatibility(args: {
  ugm: Address;
  signingPath: DelegationSigningPath;
  v21Ugms: Set<string>;
}): UgmCompatibility {
  const lower = args.ugm.toLowerCase();
  if (args.signingPath === '7715-grant' && args.v21Ugms.has(lower)) {
    return { protectable: false, reason: 'v21-incompatible-with-7715' };
  }
  return { protectable: true, reason: null };
}

/**
 * Server-side validation helper used by `/api/protection/enable`.
 * Returns the offending UGM (lower-case) if the requested
 * `protectedUgms` selection contains an entry the
 * `signingPath`/`v21Ugms` policy forbids; otherwise `null`.
 *
 * Centralised so the same rule the dialog presents to the user is the
 * one the server enforces — a hostile client can't sidestep the gate
 * by hand-rolling the POST body.
 */
export function findIncompatibleProtectedUgm(args: {
  protectedUgms: Address[];
  signingPath: DelegationSigningPath;
  v21Ugms: Set<string>;
}): string | null {
  for (const u of args.protectedUgms) {
    const r = getUgmCompatibility({
      ugm: u,
      signingPath: args.signingPath,
      v21Ugms: args.v21Ugms,
    });
    if (!r.protectable) return u.toLowerCase();
  }
  return null;
}
